/**
 * Calendar date helpers.
 *
 * Storage shape (set by worker/routes/calendar.ts):
 *   all-day  -> starts_at / ends_at are 'YYYY-MM-DD', end is INCLUSIVE
 *   timed    -> starts_at / ends_at are full UTC ISO
 *
 * Everything the family reads is in LOCAL time, so 'YYYY-MM-DD' must never be handed to
 * `new Date()` directly — that parses as UTC midnight and slides an all-day event to the
 * previous day in the Carolinas. `localDate()` is the only parser used here.
 */
import type { CalendarEvent, MemberSummary } from '../../shared/types';

/** 'YYYY-MM-DD' or full ISO -> a Date in the browser's timezone. */
export function localDate(value: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(value);
}

/** A Date -> its local 'YYYY-MM-DD' key. Used for bucketing events into days. */
export function dayKey(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function todayKey(): string {
  return dayKey(new Date());
}

export function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function addMonths(d: Date, months: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + months, 1);
}

export function monthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The 6x7 grid of days shown for a month, Sunday-first, including the spill days. */
export function monthGridDays(month: Date): Date[] {
  const first = startOfMonth(month);
  const start = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

// ---------------------------------------------------------------- events

export function eventStart(ev: CalendarEvent): Date {
  return localDate(ev.starts_at);
}

/** End as a real instant. All-day events run to the end of their last (inclusive) day. */
export function eventEnd(ev: CalendarEvent): Date {
  if (ev.all_day) {
    const last = localDate(ev.ends_at ?? ev.starts_at);
    return new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59, 999);
  }
  return localDate(ev.ends_at ?? ev.starts_at);
}

/** Every local day key an event touches (multi-day events land on each of their days). */
export function eventDayKeys(ev: CalendarEvent): string[] {
  const first = eventStart(ev);
  const lastInstant = eventEnd(ev);
  const last = new Date(
    lastInstant.getFullYear(),
    lastInstant.getMonth(),
    lastInstant.getDate()
  );

  const keys: string[] = [];
  let cursor = new Date(first.getFullYear(), first.getMonth(), first.getDate());
  // 90 is a hard stop so a malformed row can never spin the render loop.
  for (let i = 0; cursor <= last && i < 90; i++) {
    keys.push(dayKey(cursor));
    cursor = addDays(cursor, 1);
  }
  return keys.length ? keys : [dayKey(first)];
}

/** Events bucketed by local day key, each day sorted all-day first, then by start time. */
export function groupByDay(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const ev of events) {
    for (const key of eventDayKeys(ev)) {
      const list = map.get(key);
      if (list) list.push(ev);
      else map.set(key, [ev]);
    }
  }
  for (const list of map.values()) list.sort(compareEvents);
  return map;
}

/** Ordering WITHIN one day: all-day events first, then by start time, then by title. */
export function compareEvents(a: CalendarEvent, b: CalendarEvent): number {
  if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
  const diff = eventStart(a).getTime() - eventStart(b).getTime();
  return diff !== 0 ? diff : a.title.localeCompare(b.title);
}

/**
 * The day an event files under in the agenda: its start day, or today if it is already
 * under way (a trip that began yesterday belongs at the top of "up next", not buried
 * under a past date).
 */
export function agendaDayKey(ev: CalendarEvent, from = todayKey()): string {
  const keys = eventDayKeys(ev);
  return keys.find((k) => k >= from) ?? keys[0];
}

/** Day order first, then within-day order. Grouping in the agenda relies on this. */
export function sortAgenda(events: CalendarEvent[], from = todayKey()): CalendarEvent[] {
  return [...events].sort((a, b) => {
    const aKey = agendaDayKey(a, from);
    const bKey = agendaDayKey(b, from);
    if (aKey !== bKey) return aKey < bKey ? -1 : 1;
    return compareEvents(a, b);
  });
}

/** Still happening or yet to happen, soonest first. What the agenda actually shows. */
export function upcomingEvents(events: CalendarEvent[], now = new Date()): CalendarEvent[] {
  return sortAgenda(
    events.filter((ev) => eventEnd(ev).getTime() >= now.getTime()),
    dayKey(now)
  );
}

// ---------------------------------------------------------------- formatting

export function formatTime(d: Date): string {
  return d
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .replace(':00', '')
    .toLowerCase();
}

/** 'All day' | '9am' | '9am – 10:30am' */
export function formatEventTime(ev: CalendarEvent): string {
  if (ev.all_day) return 'All day';
  const start = formatTime(eventStart(ev));
  if (!ev.ends_at) return start;
  const end = eventEnd(ev);
  const sameDay = dayKey(end) === dayKey(eventStart(ev));
  return sameDay ? `${start} – ${formatTime(end)}` : `${start} – ${formatDayLabel(dayKey(end))}`;
}

/** 'Today' | 'Tomorrow' | 'Sat, Jul 18' */
export function formatDayLabel(key: string): string {
  const today = todayKey();
  if (key === today) return 'Today';
  if (key === dayKey(addDays(new Date(), 1))) return 'Tomorrow';
  return localDate(key).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** '2 minutes ago' | '3 hours ago' | 'Jul 4' — for the last-synced line. */
export function formatRelative(iso: string): string {
  const then = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(then.getTime())) return 'recently';
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------- members

/** Neutral treatment for an event that belongs to the whole house, not one person. */
export const HOUSEHOLD = { name: 'Family', color: '#6b6259' };

export function memberOf(
  ev: CalendarEvent,
  members: MemberSummary[]
): { name: string; color: string } {
  const member = ev.member_id ? members.find((m) => m.id === ev.member_id) : undefined;
  return member ?? HOUSEHOLD;
}

/** A member's color at ~10% for a chip background. Anything that isn't a plain hex
 *  (colors are user-editable in Admin) falls back to a neutral wash rather than breaking. */
export function tint(color: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}1a` : 'var(--color-surface-sunk)';
}
